import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import apiClient from '../lib/apiClient';
import supabase from '../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../lib/tenantVisibility';

// Component imports for different dashboard views
import GlobalAdminDashboard from '../components/GlobalAdminDashboard';
import PartnerAdminDashboard from '../components/PartnerAdminDashboard';

const EnterpriseAnalytics = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiConnected, setApiConnected] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [tenantsLoaded, setTenantsLoaded] = useState(false); // Track if tenants have been loaded
  
  // Separate data states for different role types
  const [globalAnalyticsData, setGlobalAnalyticsData] = useState({
    // Platform Performance & Value
    platformMetrics: null,
    partnerLeaderboard: null,
    financialMetrics: null,
    systemHealth: null,
    
    // Additional global insights
    leadProcessingTrends: null,
    partnerChurnAnalysis: null,
    featureAdoptionMetrics: null,
    revenueAnalytics: null
  });

  const [partnerAnalyticsData, setPartnerAnalyticsData] = useState({
    // Core Performance Metrics
    corePerformance: null,
    efficiencyMetrics: null,
    leadQualityMetrics: null,
    engagementMetrics: null,
    
    // Partner-specific insights
    userPerformanceLeaderboard: null,
    sourcePerformance: null,
    qualificationRules: null,
    timeBasedAnalytics: null
  });

  // Determine user's role and access level
  const userRole = user?.role;
  const isGlobalAdmin = userRole === 'global_admin';
  const isPartnerAdmin = userRole === 'partner_admin';
  const hasEnterpriseAccess = isGlobalAdmin || isPartnerAdmin;

  // Dynamic API endpoints based on role
  const apiEndpoints = useMemo(() => {
    if (isGlobalAdmin) {
      return {
        // Global Admin endpoints - cross-tenant data
        platformMetrics: '/enterprise-analytics/global/platform-performance',
        partnerLeaderboard: '/enterprise-analytics/global/partner-leaderboard',
        financialMetrics: '/enterprise-analytics/global/financial-metrics',
        systemHealth: '/enterprise-analytics/global/system-health',
        leadProcessingTrends: '/enterprise-analytics/global/lead-trends',
        partnerChurnAnalysis: '/enterprise-analytics/global/partner-churn',
        featureAdoptionMetrics: '/enterprise-analytics/global/feature-adoption',
        revenueAnalytics: '/enterprise-analytics/global/revenue-analytics'
      };
    } else if (isPartnerAdmin) {
      return {
        // Partner Admin endpoints - tenant-scoped data
        corePerformance: '/enterprise-analytics/partner/core-performance',
        efficiencyMetrics: '/enterprise-analytics/partner/efficiency-metrics',
        leadQualityMetrics: '/enterprise-analytics/partner/lead-quality',
        engagementMetrics: '/enterprise-analytics/partner/engagement',
        userPerformanceLeaderboard: '/enterprise-analytics/partner/user-leaderboard',
        sourcePerformance: '/enterprise-analytics/partner/source-performance',
        qualificationRules: '/enterprise-analytics/partner/qualification-rules',
        timeBasedAnalytics: '/enterprise-analytics/partner/time-based-analytics'
      };
    }
    return {};
  }, [isGlobalAdmin, isPartnerAdmin]);

  // Use useCallback to memoize the function and prevent unnecessary re-renders
  const fetchTenants = useCallback(async () => {
    if (!isGlobalAdmin || tenantsLoaded) return;
    
    try {
      console.log('🔍 Fetching tenants...');
      
      const { data: { session } } = await supabase.auth.getSession();
      console.log('🔍 Supabase session when fetching tenants:', session ? 'EXISTS' : 'NONE');
      console.log('🔍 User email:', session?.user?.email);
      console.log('🔍 User role from metadata:', session?.user?.user_metadata?.role || session?.user?.app_metadata?.role);
      
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name, industry, plan, subscription_status, is_demo')
        .or(VISIBLE_TENANTS_OR) // hide inactive/suspended from global_admin dropdown
        .order('name');
      
      console.log('🔍 Tenants query result:', { data, error });
      
      if (error) throw error;
      
      if (data) {
        setTenants(data);
        setTenantsLoaded(true); // Mark as loaded
        console.log('✅ Tenants loaded:', data.length);
      }
    } catch (err) {
      console.error('❌ Could not fetch tenants:', err);
      // Don't set error state here, just log it - this shouldn't block the UI
    }
  }, [isGlobalAdmin, tenantsLoaded]);

  // Test API connectivity
  useEffect(() => {
    if (hasEnterpriseAccess && user && !apiConnected) {
      testApiConnectivity();
    }
  }, [user, hasEnterpriseAccess, apiConnected]);

  // Fetch tenants only once when user and role are available
  useEffect(() => {
    if (isGlobalAdmin && user && !tenantsLoaded) {
      fetchTenants();
    }
  }, [user, isGlobalAdmin, tenantsLoaded, fetchTenants]);

  const testApiConnectivity = async () => {
    try {
      setLoading(true);
      setError(null);

      // Test connectivity with role-appropriate endpoint
      const testEndpoint = isGlobalAdmin 
        ? '/enterprise-analytics/global/health-check'
        : '/enterprise-analytics/partner/health-check';

      const response = await apiClient.get(testEndpoint, {
        timeout: 8000,
        headers: {
          'Content-Type': 'application/json',
          'X-User-Role': userRole
        }
      });

      if (response.data.success) {
        setApiConnected(true);
        console.log(`✅ Enterprise Analytics API connected for ${userRole}`);
        
        // Pass forceLoad=true to bypass the state check
        await loadAnalyticsData(true);
      }

    } catch (err) {
      console.warn(`⚠️ Enterprise Analytics API not available for ${userRole}:`, err.message);
      setApiConnected(false);
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async (forceLoad = false, tenantId = null) => {
    if (!forceLoad && (!apiConnected || !hasEnterpriseAccess)) {
      console.log('API not connected or insufficient access, skipping data load');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log(`📊 Loading ${userRole} analytics data...`);

      // Create requests array based on user role
      const requests = Object.entries(apiEndpoints).map(([key, endpoint]) => 
        apiClient.get(endpoint, {
          headers: {
            'X-User-Role': userRole,
            'X-Tenant-Scope': isPartnerAdmin ? user.tenant_id : ((tenantId || selectedTenant) !== 'all' ? (tenantId || selectedTenant) : 'global')
          }
        }).catch(e => ({ 
          error: e, 
          data: null, 
          endpoint: key 
        }))
      );

      const responses = await Promise.all(requests);

      // Process responses based on role
      if (isGlobalAdmin) {
        const processedData = processGlobalAdminResponses(responses);
        setGlobalAnalyticsData(processedData);
      } else if (isPartnerAdmin) {
        const processedData = processPartnerAdminResponses(responses);
        setPartnerAnalyticsData(processedData);
      }

      console.log(`✅ ${userRole} analytics data loaded successfully`);

    } catch (err) {
      console.error(`❌ Failed to load ${userRole} analytics:`, err);
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const processGlobalAdminResponses = (responses) => {
    const processResponse = (response, endpointName) => {
      if (response.error) {
        const status = response.error.response?.status;
        const message = response.error.message;
        
        // Log different types of errors differently
        if (status === 500) {
          console.error(`❌ Server error for ${endpointName}:`, message);
        } else if (status === 404) {
          console.warn(`⚠️ Endpoint not found (${endpointName}):`, message);
        } else {
          console.warn(`⚠️ API endpoint failed (${endpointName}):`, message);
        }
        
        return null;
      }
      return response.data?.data || null;
    };

    return {
      platformMetrics: processResponse(responses[0], 'platformMetrics'),
      partnerLeaderboard: processResponse(responses[1], 'partnerLeaderboard'),
      financialMetrics: processResponse(responses[2], 'financialMetrics'),
      systemHealth: processResponse(responses[3], 'systemHealth'),
      leadProcessingTrends: processResponse(responses[4], 'leadProcessingTrends'),
      partnerChurnAnalysis: processResponse(responses[5], 'partnerChurnAnalysis'),
      featureAdoptionMetrics: processResponse(responses[6], 'featureAdoptionMetrics'),
      revenueAnalytics: processResponse(responses[7], 'revenueAnalytics')
    };
  };

  const processPartnerAdminResponses = (responses) => {
    const processResponse = (response, endpointName) => {
      if (response.error) {
        const status = response.error.response?.status;
        const message = response.error.message;
        
        // Log different types of errors differently
        if (status === 500) {
          console.error(`❌ Server error for ${endpointName}:`, message);
        } else if (status === 404) {
          console.warn(`⚠️ Endpoint not found (${endpointName}):`, message);
        } else {
          console.warn(`⚠️ API endpoint failed (${endpointName}):`, message);
        }
        
        return null;
      }
      return response.data?.data || null;
    };

    return {
      corePerformance: processResponse(responses[0], 'corePerformance'),
      efficiencyMetrics: processResponse(responses[1], 'efficiencyMetrics'),
      leadQualityMetrics: processResponse(responses[2], 'leadQualityMetrics'),
      engagementMetrics: processResponse(responses[3], 'engagementMetrics'),
      userPerformanceLeaderboard: processResponse(responses[4], 'userPerformanceLeaderboard'),
      sourcePerformance: processResponse(responses[5], 'sourcePerformance'),
      qualificationRules: processResponse(responses[6], 'qualificationRules'),
      timeBasedAnalytics: processResponse(responses[7], 'timeBasedAnalytics')
    };
  };

  const handleApiError = (err) => {
    const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
    
    if (err.response?.status === 401) {
      setError('Authentication required. Please log in again.');
    } else if (err.response?.status === 403) {
      setError(`Access denied. ${userRole} role may not have sufficient permissions.`);
    } else if (err.code === 'ECONNABORTED') {
      setError('Request timed out. The analytics service may be experiencing high load.');
    } else if (err.response?.status === 500) {
      // Don't show 500 errors to users - log them but continue
      console.error('Server error occurred, but continuing with available data');
    } else {
      setError(`Failed to load analytics: ${errorMessage}`);
    }
  };

  const handleRefreshData = async () => {
    try {
      setRefreshing(true);
      console.log(`🔄 Refreshing ${userRole} analytics...`);

      // Refresh materialized views if supported
      try {
        const refreshEndpoint = isGlobalAdmin 
          ? '/enterprise-analytics/global/refresh-views'
          : '/enterprise-analytics/partner/refresh-views';
          
        await apiClient.post(refreshEndpoint, {}, {
          headers: {
            'X-User-Role': userRole,
            'X-Tenant-Scope': isPartnerAdmin ? user.tenant_id : 'global'
          }
        });
        console.log('✅ Analytics views refreshed');
      } catch (refreshErr) {
        console.warn('⚠️ Could not refresh views (may not have permission):', refreshErr.message);
      }

      await loadAnalyticsData(true);
      console.log(`✅ ${userRole} analytics refreshed successfully`);

    } catch (err) {
      console.error(`❌ Failed to refresh ${userRole} analytics:`, err);
      handleApiError(err);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle authentication loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Enterprise Analytics</h2>
          <p className="text-gray-600">Verifying permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect if insufficient permissions
  if (!hasEnterpriseAccess) {
    return <Navigate to="/business-analytics" replace />;
  }

  // Handle initial loading state
  if (loading && !apiConnected && 
      (Object.values(globalAnalyticsData).every(v => v === null) && 
       Object.values(partnerAnalyticsData).every(v => v === null))) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Connecting to {isGlobalAdmin ? 'Global' : 'Partner'} Analytics
          </h2>
          <p className="text-gray-600">
            {isGlobalAdmin 
              ? 'Loading cross-platform insights and partner performance data...'
              : 'Loading your network performance and team analytics...'
            }
          </p>
        </div>
      </div>
    );
  }

  // Handle authentication errors
  if (error && (error.includes('Authentication') || error.includes('Access denied'))) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <h2 className="font-bold mb-2">Access Error</h2>
            <p>{error}</p>
          </div>
          <div className="space-x-2">
            <button
              onClick={() => window.location.href = '/login'}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
            >
              Login Again
            </button>
            <button
              onClick={() => setError(null)}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
            >
              Continue with Demo Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render role-specific dashboard
  const commonProps = {
    loading: loading || refreshing,
    apiConnected,
    onRefreshData: handleRefreshData,
    onLoadData: loadAnalyticsData,
    error,
    user,
    tenants,
    selectedTenant,
    onTenantChange: (tenantId) => {
      setSelectedTenant(tenantId);
      loadAnalyticsData(true, tenantId);
    }
  };

  if (isGlobalAdmin) {
    return (
      <GlobalAdminDashboard
        data={globalAnalyticsData}
        {...commonProps}
      />
    );
  }

  if (isPartnerAdmin) {
    return (
      <PartnerAdminDashboard
        data={partnerAnalyticsData}
        {...commonProps}
      />
    );
  }

  // Fallback (shouldn't reach here due to earlier checks)
  return <Navigate to="/business-analytics" replace />;
};

export default EnterpriseAnalytics;
